import React, { useState, useRef, useEffect, useMemo } from 'react';
import { css } from '@compiled/react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/new';

import type {
	CommentLocation,
	CommentReply,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery';
import {
	CommentType,
	type CommentData,
	type ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData';
import {
	useCommentsContentActions,
	useCommentsContentState,
} from '@confluence/comment-context/entry-points/useCommentsContent';
import type { PageMode } from '@confluence/page-utils/entry-points/enums';
import type { PageInfoNode } from '@confluence/page-info/entry-points/PageInfoQuery';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { getValidDate } from '@confluence/comments-util/entry-points/dateUtils';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { WithCurvedBranchingStyle } from '@confluence/inline-comments-common/entry-points/components';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import { useCommentsPanel } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics';
import { RepliesToggler } from '@confluence/comments-common/entry-points/RepliesToggler';
import { RepliesFooter } from '@confluence/comments-common/entry-points/RepliesFooter';
import { useUpdateVisibleReplies } from '@confluence/comments-common/entry-points/useUpdateVisibleReplies';
import {
	ViewReplyOptions,
	DEFAULT_NUM_VISIBLE_REPLIES,
} from '@confluence/comments-common/entry-points/repliesHelpers';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { fg } from '@confluence/feature-gating';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments';
import { expValEquals } from '@confluence/feature-experiments';

import { ObjectCommentNestableReplies } from './ObjectCommentNestableReplies';
import { ObjectCommentReplyEditor } from './ObjectCommentReplyEditor';

const commentThreadRepliesBoxStyles = css({
	display: 'flex',
	flexDirection: 'column',
	paddingLeft: token('space.200'),
	paddingRight: token('space.100'),
});

const translucentStyle = css({
	opacity: '50%',
});

const replyActionButtonStyles = {
	marginLeft: token('space.150'),
	width: '62px',
	height: '24px',
	left: '-12px',
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
};

const customizedReplyButtonStyles = css({
	'&::after': {
		borderStyle: 'none',
	},
});

export type ObjectCommentRepliesProps = {
	parentComment: CommentData;
	pageInfo: PageInfoNode | null;
	isCurrentSelectedComment: boolean;
	hoveredCommentId: string;
	pageMode: PageMode;
	parentCommentId: string;
	handleMouseEnter: (id?: string) => void;
	handleMouseLeave: () => void;
	canAddComments: boolean;
	isOffline?: boolean;
	flags?: FlagsStateContainer;
};

const i18n = defineMessages({
	replyAction: {
		id: 'object-comments.reply.action',
		defaultMessage: 'Reply',
		description: 'Reply button label for comment thread in comments panel',
	},
});

export const ObjectCommentReplies = ({
	parentComment,
	pageInfo,
	isCurrentSelectedComment,
	hoveredCommentId,
	pageMode,
	parentCommentId,
	handleMouseEnter,
	handleMouseLeave,
	canAddComments,
	isOffline = false,
	flags,
}: ObjectCommentRepliesProps) => {
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();

	const [
		{ commentsMarkedAsRead, replyToCommentId, location },
		{ setCommentsInThreadToBeMarkedAsRead, setReplyToCommentId },
	] = useCommentsPanel();

	const allReplies = useMemo(() => {
		return parentComment.replies.filter((r) => (r as CommentReply) !== null);
	}, [parentComment.replies]);

	const numOfReplies = allReplies.length;

	const { unreadCommentIDSet } = useUnreadCommentsState();

	const unreadReplyIds = useMemo(() => {
		return allReplies
			.filter((r) =>
				fg('confluence_frontend_unread_comments_fixes') ? unreadCommentIDSet.has(r.id) : r.isUnread,
			)
			.map((r) => r.id);
	}, [allReplies, unreadCommentIDSet]);

	// Show the latest replies in the thread by default
	const [shouldShowLatestReplies, setShouldShowLatestReplies] = useState(true);
	const [visibleReplies, setVisibleReplies] = useState<ReplyData[]>([]);

	const { showModal } = useDialogs();

	const commentType = parentComment.type;
	const isParentCommentOpen = parentComment.isOpen;

	const [currentReplyView, setReplyView] = useState(
		// On the panel view we use a function for this
		// On the object comments we default to all no matter what
		ViewReplyOptions.ALL,
	);

	// User page permissions

	// The user can upload media only if they have update permissions for the page

	const threadKey =
		commentType === CommentType.INLINE
			? (parentComment?.location as CommentLocation).inlineMarkerRef || ''
			: parentCommentId;

	const supportedTopLevelActions: CommentAction[] = isParentCommentOpen
		? ['edit', 'resolve', 'delete']
		: ['reopen']; // reopen won't be rendered for reply, but we are using it to render disabled reaction summary for replies

	const expandReplyEditor =
		parentCommentId === replyToCommentId && location === LocationValue.FOOTER;

	const visibleUnreadRepliesNotMarkedAsRead = useMemo(() => {
		if (fg('confluence_frontend_unread_comments_fixes')) {
			return [];
		}

		return visibleReplies
			.filter((reply) => {
				const markedReplies = commentsMarkedAsRead[commentType][threadKey];
				// Check if the reply id is not in the markedReplies set
				return (!markedReplies || !markedReplies.has(reply.id)) && reply.isUnread;
			})
			.map((reply) => reply.id);
	}, [visibleReplies, commentsMarkedAsRead, threadKey, commentType]);

	const numOfHiddenReplies = useRef(
		currentReplyView === ViewReplyOptions.ALL ? 0 : numOfReplies - visibleReplies.length,
	);

	const topLevelReplyToNestedChildrenMap = useMemo(() => {
		const topLevelReplyIds = new Set(
			allReplies.filter((reply) => reply.parentId === parentCommentId).map((reply) => reply.id),
		);

		// We need to get all replies that are nested to any depth under a top level reply.
		// This will help us render the GeneralReplyScroller component for each reply (mainly, we need to show user icons and the blue dot indicator)
		const topLevelReplyToNestedChildrenMapInternal: { [key: string]: ReplyData[] } = Array.from(
			topLevelReplyIds,
		).reduce<{ [key: string]: ReplyData[] }>((acc, replyId) => {
			acc[replyId] = [];
			return acc;
		}, {});

		const replyIdToReplyMap = allReplies.reduce<{ [key: string]: ReplyData }>((acc, reply) => {
			acc[reply.id] = reply;
			return acc;
		}, {});

		// Map all replies to their top level parent. Replies can be nested to any depth, so we need to traverse the tree.
		const filteredReplies = allReplies.filter((reply) => !topLevelReplyIds.has(reply.id));

		for (const filteredReply of filteredReplies) {
			let replyParentId = filteredReply.parentId;

			// Add a circuit breaker to prevent infinite loops if we get really wonky data
			let circuitBreaker = 0;
			while (replyParentId && circuitBreaker < 100 && !topLevelReplyIds.has(replyParentId)) {
				replyParentId = replyIdToReplyMap[replyParentId]?.parentId;
				circuitBreaker++;
			}

			if (replyParentId && replyParentId in topLevelReplyToNestedChildrenMapInternal) {
				// Second condition will always be true, unless we somehow hit circuit breaker above
				topLevelReplyToNestedChildrenMapInternal[replyParentId].push(filteredReply);
			}
		}

		return topLevelReplyToNestedChildrenMapInternal;
	}, [allReplies, parentCommentId]);

	// Determine the latest top level replies to show on the thread's initial load
	const latestReplies = useMemo(() => {
		const replies: ReplyData[] = [];
		const topLevelReplies = allReplies.filter(
			(reply) => reply.id in topLevelReplyToNestedChildrenMap,
		);

		// Sort by createdAtNonLocalized value
		topLevelReplies.sort((a, b) => {
			// Safely handle missing or invalid dates
			const dateA = getValidDate(a?.createdAtNonLocalized);
			const dateB = getValidDate(b?.createdAtNonLocalized);

			return dateA.getTime() - dateB.getTime();
		});

		// Add the latest replies to the replies array
		for (let i = 0; i < DEFAULT_NUM_VISIBLE_REPLIES; i++) {
			if (topLevelReplies.length > 0) {
				const reply = topLevelReplies.pop();
				if (reply) {
					// Unshift to maintain reply chronological order
					replies.unshift(reply);
				}
			}
		}

		return replies;
	}, [allReplies, topLevelReplyToNestedChildrenMap]);

	/*
	 * Determine the latest top level reply IDs and their nested reply IDs.
	 * This is passed to RepliesToggler to determine the hidden replies.
	 */
	const latestReplyIds = useMemo(() => {
		const latestAndNestedReplyIds: Set<string> = new Set();

		for (const reply of latestReplies) {
			latestAndNestedReplyIds.add(reply.id);

			for (const nestedReply of topLevelReplyToNestedChildrenMap[reply.id]) {
				latestAndNestedReplyIds.add(nestedReply.id);
			}
		}

		return latestAndNestedReplyIds;
	}, [latestReplies, topLevelReplyToNestedChildrenMap]);

	const parentToRepliesMap = useMemo(() => {
		const parentToRepliesMapInternal: { [key: string]: ReplyData[] } = {
			[parentCommentId]: [],
			...allReplies.reduce(
				(acc, reply) => ({
					...acc,
					[reply.id]: [],
				}),
				{},
			),
		};

		for (const reply of allReplies) {
			if (reply.parentId && reply.parentId in parentToRepliesMapInternal) {
				// Second condition will always be true, unless we get really wonky data
				parentToRepliesMapInternal[reply.parentId].push(reply);
			}
		}

		return parentToRepliesMapInternal;
	}, [allReplies, parentCommentId]);

	// if selecting the comment thread, we need to mark all visible replies as read as well
	// we'll let setCommentsInThreadToBeMarkedAsRead handle duplicates if any
	useEffect(() => {
		if (fg('confluence_frontend_unread_comments_fixes')) {
			return;
		}

		if (isCurrentSelectedComment) {
			setCommentsInThreadToBeMarkedAsRead({
				threadKey,
				commentIds: visibleUnreadRepliesNotMarkedAsRead,
				commentType,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- there might be a better way to handle this without needing a useEFfect
	}, [isCurrentSelectedComment]);

	useUpdateVisibleReplies({
		allReplies,
		commentType,
		isParentCommentOpen,
		currentReplyView,
		latestReplies,
		latestReplyIds,
		parentCommentId,
		setVisibleReplies,
		shouldShowLatestReplies,
		numOfHiddenReplies,
		allowNestedReplies: true,
	});

	const handleReplyButtonClick = () => {
		if (hasContentChanged) {
			// if the content has changed and the user has clicked reply button to another comment thread, warn the user
			showModal(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					setReplyToCommentId({ parentCommentId, location: LocationValue.FOOTER });
				},
			});
		} else {
			setReplyToCommentId({ parentCommentId, location: LocationValue.FOOTER });
		}
	};

	const canShowReplyButton = isParentCommentOpen && canAddComments;

	const renderFooter = () =>
		expandReplyEditor ? (
			<ObjectCommentReplyEditor
				pageInfo={pageInfo}
				pageMode={pageMode}
				setReplyView={setReplyView}
				parentComment={parentComment}
				threadKey={threadKey}
				replyToId={parentCommentId}
				isOffline={isOffline}
			/>
		) : (
			canShowReplyButton && (
				<WithCurvedBranchingStyle
					showDefaultStyles
					customStyles={replyActionButtonStyles}
					testId="comments-panel-reply-comment-action"
					onClick={handleReplyButtonClick}
				>
					<Button
						data-cy="comments-panel-reply-comment-action"
						css={customizedReplyButtonStyles}
						appearance="subtle"
						isDisabled={!isParentCommentOpen || isOffline}
						analyticsContext={{
							attributes: {
								name: 'replyToCommentButton',
								source: AnalyticsSource.COMMENTS_PANEL,
							},
						}}
						interactionName="comments-panel-reply-comment-action"
					>
						<FormattedMessage {...i18n.replyAction} />
					</Button>
				</WithCurvedBranchingStyle>
			)
		);

	const renderRepliesToggler = () => {
		return (
			<RepliesToggler
				replies={allReplies}
				setVisibleReplies={setVisibleReplies}
				unreadReplyIds={unreadReplyIds}
				setReplyView={setReplyView}
				threadKey={threadKey}
				currentReplyView={currentReplyView}
				numOfHiddenReplies={numOfHiddenReplies}
				commentType={commentType}
				parentCommentId={parentCommentId}
				isParentOpen={isParentCommentOpen}
				canAddComments={canAddComments}
				shouldShowLatestReplies={shouldShowLatestReplies}
				setShouldShowLatestReplies={setShouldShowLatestReplies}
				latestReplyIds={latestReplyIds}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				analyticsSource={AnalyticsSource.PAGE_FOOTER}
			/>
		);
	};

	return (
		<>
			{numOfReplies > 0 &&
				expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
				renderRepliesToggler()}
			<div
				data-testid={`${LocationValue.FOOTER}-comment-thread-${parentComment.id}-replies`}
				css={[
					commentThreadRepliesBoxStyles,
					parentComment.wasRemovedByAnotherUser && translucentStyle,
				]}
			>
				{numOfReplies > 0 &&
					!expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
					renderRepliesToggler()}
				{visibleReplies.length > 0 && (
					<ObjectCommentNestableReplies
						parentReplyId={parentCommentId}
						parentToRepliesMap={parentToRepliesMap}
						handleMouseEnter={handleMouseEnter}
						handleMouseLeave={handleMouseLeave}
						canAddComments={canAddComments}
						pageInfo={pageInfo}
						pageMode={pageMode}
						threadKey={threadKey}
						supportedTopLevelActions={supportedTopLevelActions}
						hoveredCommentId={hoveredCommentId}
						isTopLevelReply
						parentComment={parentComment}
						parentReplyButtonAllowed={canShowReplyButton}
						setReplyView={setReplyView}
						isOffline={isOffline}
						flags={flags}
					/>
				)}
				{expValEquals('confluence_comments_tuneup', 'isEnabled', true) ? (
					<RepliesFooter
						replyView={currentReplyView}
						setReplyView={setReplyView}
						expandReplyEditor={expandReplyEditor}
						isParentCommentOpen={isParentCommentOpen}
						handleReplyButtonClick={handleReplyButtonClick}
						numOfVisibleReplies={visibleReplies ? visibleReplies.length : 0}
						isOffline={isOffline}
					>
						<ObjectCommentReplyEditor
							pageInfo={pageInfo}
							pageMode={pageMode}
							setReplyView={setReplyView}
							parentComment={parentComment}
							threadKey={threadKey}
							replyToId={parentCommentId}
							isOffline={isOffline}
						/>
					</RepliesFooter>
				) : (
					renderFooter()
				)}
			</div>
		</>
	);
};
